import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  HStack,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Spacer,
} from "@chakra-ui/react";

export default function EntityList({
  title,
  subtitle,
  items = [],
  columns = [],
  onNew,
  onRowClick,
  defaultRowsPerPage = 25,
  showNew = true,
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [page, setPage] = useState(1);
  const [hovered, setHovered] = useState(null);
  const searchRef = useRef(null);

  // keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current && searchRef.current.focus();
      }
      if ((e.key === "N" || e.key === "n") && onNew) {
        e.preventDefault();
        onNew();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onNew]);

  // debounce search
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = items.filter((it) => {
    const q = (debounced || "").trim().toLowerCase();
    if (q) {
      const hay = Object.values(it).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (status) {
      if ((it.status || "").toUpperCase() !== status.toUpperCase())
        return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => setPage(1), [debounced, status, rowsPerPage]);

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Box>
          <Text fontSize="lg" fontWeight="semibold">
            {title}
          </Text>
          {subtitle && (
            <Text fontSize="13px" color="gray.500">
              {subtitle}
            </Text>
          )}
        </Box>
        {showNew && (
          <Button colorScheme="teal" size="sm" onClick={onNew}>
            + New
          </Button>
        )}
      </HStack>

      <Flex mb={3} align="center">
        <HStack spacing={3}>
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            ref={searchRef}
          />
          <Select
            size="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="BLOCKED">Blocked</option>
          </Select>
          {(search || status) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStatus("");
              }}
            >
              Clear
            </Button>
          )}
        </HStack>
        <Spacer />
      </Flex>

      <Box borderWidth={1} borderRadius="6px" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              {columns.map((c) => (
                <Th key={c.key} textAlign={c.align || "left"}>
                  {c.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {visible.map((it) => (
              <Tr
                key={it._id}
                onMouseEnter={() => setHovered(it._id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onRowClick && onRowClick(it)}
                _hover={{ bg: "gray.50", cursor: "pointer" }}
                height="44px"
              >
                {columns.map((c) => (
                  <Td key={c.key} textAlign={c.align || "left"}>
                    {c.render ? c.render(it) : it[c.key]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Flex
          align="center"
          justify="space-between"
          p={3}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack>
            <Select
              size="sm"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Select>
            <Text fontSize="13px" color="gray.600">
              Rows per page
            </Text>
          </HStack>
          <Text fontSize="13px" color="gray.600">
            Page {page} of {totalPages}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}
